
import { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

interface ToastProps {
  message: string
  type: ToastType
  isVisible: boolean
  onClose: () => void
  duration?: number // in milliseconds
}

const toastConfig = {
  success: {
    icon: CheckCircle,
    bgColor: 'bg-status-success-bg',
    borderColor: 'border-status-success-border',
    textColor: 'text-status-success-text',
    iconColor: 'text-status-success-text'
  },
  error: {
    icon: AlertCircle,
    bgColor: 'bg-status-error-bg',
    borderColor: 'border-status-error-border',
    textColor: 'text-status-error-text',
    iconColor: 'text-status-error-text'
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-status-warning-bg',
    borderColor: 'border-status-warning-border',
    textColor: 'text-status-warning-text',
    iconColor: 'text-status-warning-text'
  },
  info: {
    icon: Info,
    bgColor: 'bg-background-secondary',
    borderColor: 'border-border-primary',
    textColor: 'text-text-primary',
    iconColor: 'text-brand-primary'
  }
}

export function Toast({ message, type, isVisible, onClose, duration = 4000 }: ToastProps) {
  const config = toastConfig[type]
  const IconComponent = config.icon

  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose()
      }, duration)

      return () => clearTimeout(timer)
    }
  }, [isVisible, duration, onClose])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`
            ${config.bgColor} ${config.borderColor} ${config.textColor}
            border rounded-xl p-4 shadow-lg backdrop-blur-sm max-w-sm
          `}
        >
          <div className="flex items-start space-x-3">
            <IconComponent className={`w-5 h-5 ${config.iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <p className="text-sm font-medium">{message}</p>
            </div>
            <button
              onClick={onClose}
              className={`${config.iconColor} hover:opacity-70 flex-shrink-0 transition-opacity`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}